import { RollPlanStatus } from '../../services/pairTradingApi';

/**
 * Which futures contract the page should chart and measure the basis against.
 *
 * The answer is "the one the pair actually trades", and that is NOT the front-month:
 * the roll runs on the session BEFORE expiry, so for one whole day the bot is on the
 * next contract while `/search/futures` still returns the old one. The chart and the
 * basis used to read that search default — loaded once on mount and never revisited —
 * so after a roll the page drew a contract the bot had already left.
 */
export type ChartContractSource = 'engine' | 'config' | 'default';

export interface ChartContract {
  /** Bare symbol (no exchange prefix), or null when nothing is known yet. */
  symbol: string | null;
  source: ChartContractSource;
  /**
   * The engine trades a different contract from the one the DB records — its roll
   * report never landed. The engine wins (it is the one placing orders) and the caller
   * surfaces this, because the two disagreeing is a state someone has to repair.
   */
  engineAheadOfConfig: boolean;
  /** What the config records, kept so a disagreement can name both sides. */
  configSymbol: string | null;
}

const bare = (value: unknown): string | null => {
  const text = typeof value === 'string' ? value.trim() : '';
  if (!text) return null;
  return text.split(':').pop() || null;
};

/**
 * Priority: what the worker says it trades, then what the config records, then the
 * front-month default (only meaningful before a pair exists).
 *
 * Deliberately not time-based. "It is past 14:00 on the roll date, so show F2" would
 * lie whenever the bot was stopped through its own roll window — the contract did not
 * move, and the page must keep showing what would actually trade if it were started.
 */
export function resolveChartContract(args: {
  rollStatus: RollPlanStatus | null;
  /** `deriv_symbol` from the loaded pair config — same value, available sooner. */
  configSymbol?: string | null;
  /** Front-month from the instrument search, used only as a last resort. */
  defaultSymbol?: string | null;
}): ChartContract {
  const { rollStatus, configSymbol, defaultSymbol } = args;
  const engine = rollStatus?.engine as { contract?: unknown } | null | undefined;
  const engineSymbol = bare(engine?.contract);
  const dbSymbol = bare(rollStatus?.derivSymbol) ?? bare(configSymbol);

  if (engineSymbol) {
    return {
      symbol: engineSymbol,
      source: 'engine',
      engineAheadOfConfig: Boolean(dbSymbol) && dbSymbol !== engineSymbol,
      configSymbol: dbSymbol,
    };
  }
  if (dbSymbol) {
    return { symbol: dbSymbol, source: 'config', engineAheadOfConfig: false, configSymbol: dbSymbol };
  }
  return {
    symbol: bare(defaultSymbol),
    source: 'default',
    engineAheadOfConfig: false,
    configSymbol: null,
  };
}

/**
 * One line telling the operator the page is not charting the front-month.
 *
 * Silent while the charted contract IS the search default — that is the ordinary case
 * and a permanent note there would be noise. It speaks exactly when the switch would
 * otherwise be invisible: after a roll, when the bot is a contract ahead of the list
 * everything else on the page is built from.
 */
export function chartContractNote(args: {
  contract: ChartContract;
  /** Front-month from the instrument search — what the operator would otherwise expect. */
  defaultSymbol?: string | null;
}): string | null {
  const { contract } = args;
  if (!contract.symbol) return null;
  if (contract.engineAheadOfConfig && contract.configSymbol) {
    return `Charting ${contract.symbol} — the bot has rolled, the saved config still says ${contract.configSymbol}`;
  }
  const fallback = bare(args.defaultSymbol);
  if (!fallback || fallback === contract.symbol) return null;
  return `Charting ${contract.symbol}, the contract this pair trades (front-month is ${fallback})`;
}

/**
 * Poll interval for the roll status, in ms.
 *
 * Normally a minute — the plan moves on human timescales. Around the pair's OWN roll
 * time it drops to ten seconds, so the chart follows the contract within seconds of the
 * switch instead of drawing the dead one for another minute. The window comes from the
 * plan, not from a constant: the operator can move `futures_roll_time`.
 */
export function rollPollIntervalMs(args: {
  status: RollPlanStatus | null;
  /** Minutes since midnight, app-local. Injected so this stays pure. */
  nowMinutes: number;
}): number {
  const SLOW = 60_000;
  const FAST = 10_000;
  const plan = args.status?.plan;
  if (!plan || plan.mode === 'OFF') return SLOW;
  if (!plan.rollDate || plan.rollDate !== args.status?.today) return SLOW;
  const match = /^(\d{1,2}):(\d{2})$/.exec(String(plan.rollTime ?? '').trim());
  if (!match) return SLOW;
  const target = Number(match[1]) * 60 + Number(match[2]);
  const from = target - 5;
  const to = target + 15;
  return args.nowMinutes >= from && args.nowMinutes <= to ? FAST : SLOW;
}
